import React, { useState } from 'react';
import _ from 'lodash';

import Button from '../../themed/button';
import Confirm from '../../themed/confirm';
import Conditional from '../../common/conditional';
import ExcelExport from '../../common/excelExport';
import Modal from '../../themed/modal';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';
import Table from '../../common/table';

import css from '../helideck-inspection/style.module.scss';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface AutGridTableProps<T> {
	data: T[];
	tableColumns: models.Column[];
	onItemsReset?: (items: any) => void;
	onDeleteItem: (item: any) => void;
	onEditItem?: (item: any) => void;
	onMoveItem?: (item: any) => void;
}

const AutTable = <T extends unknown>(props: AutGridTableProps<T>) => {
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const [confirmVisible, setConfirmVisible] = useState(false);
	const [itemToDelete, setItemToDelete] = useState<T | null>(null);

	const getPageSizes = (): models.SelectOption[] => {
		return utils.getPageRange(20).map((page) => ({ id: page, name: page }));
	};

	const getPaginatedData = () => {
		const sorted = _.orderBy(props.data, [sortColumn.path], [sortColumn.order]);
		const paginated = utils.paginate(sorted, currentPage, pageSize);

		return { totalCount: props.data.length, paginated };
	};

	const getActualTableColumns = () => {
		const actualTableColumns = [...props.tableColumns];

		const deleteColumn: models.Column = {
			path: 'null',
			label: '',
			content: (item: any) => (
				<button
					className={css['delete-item']}
					onClick={() => setItemToDelete(item)}
				>
					<i className="bi bi-trash"></i>
				</button>
			),
		};

		actualTableColumns.push(deleteColumn);

		if (props.onEditItem) {
			const editColumn: models.Column = {
				path: 'idx',
				label: '',
				content: (item: any) => (
					<button
						className={css['edit-item']}
						onClick={() => (props.onEditItem ? props.onEditItem(item) : null)}
					>
						<i className="bi bi-pencil"></i>
					</button>
				),
			};

			actualTableColumns.push(editColumn);
		}

		if (props.onMoveItem) {
			const moveColumn: models.Column = {
				path: 'null',
				label: '',
				content: (item: any) => (
					<button
						className={css['move-item']}
						onClick={() => props?.onMoveItem?.(item)}
					>
						<i className="bi bi-clipboard-data-fill"></i>
					</button>
				),
			};

			actualTableColumns.push(moveColumn);
		}

		return actualTableColumns;
	};

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = parseInt(e.target.value);
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

	
	const paginatedData = getPaginatedData();

	return (
		<Conditional isVisible={props.data.length > 0}>
			<Conditional isVisible={props.onItemsReset !== undefined}>
				<Conditional isVisible={!confirmVisible}>
					<div className="d-flex justify-content-end align-items-center">
						<Button
							style={{ backgroundColor: 'red' }}
							icon="trash"
							label="Clear Items"
							onClick={() => setConfirmVisible(true)}
						/>
					</div>
				</Conditional>

				<Conditional isVisible={confirmVisible}>
					<Confirm
						title="Confirm"
						message={`Are you sure you want to clear these items? This cannot be undone`}
						onCancel={() => setConfirmVisible(false)}
						onConfirm={() => props.onItemsReset ? props.onItemsReset([]) : null}
					/>
				</Conditional>
			</Conditional>

			<ExcelExport data={props.data} fileName='Excel Export'>
				<div className="mt-3 table-responsive">
					<Table
						columns={getActualTableColumns()}
						data={paginatedData.paginated}
						sortColumn={sortColumn}
						onSort={setSortColumn}
						isFetching={false}
					/>

					<div className="d-flex justify-content-between align-items-center mt-4">
						<Select
							name="page-size"
							placeholder={'Showing (' + pageSize + 'rows)'}
							options={getPageSizes()}
							value={pageSize}
							onChange={handlePageSizeChange}
						/>

						<Pagination
							itemsCount={paginatedData.totalCount}
							pageSize={pageSize}
							currentPage={currentPage}
							onPageChange={(page) => setCurrentPage(page)}
						/>
					</div>
				</div>
			</ExcelExport>

			<Modal style={{ height: "auto" }} isVisible={!!itemToDelete} onClose={() => setItemToDelete(null)}>
				<div className="p-1">
					<Confirm
						title="Warning"
						message="Are you sure you want to delete this item?"
						onConfirm={() => props.onDeleteItem(itemToDelete)}
						onCancel={() => setItemToDelete(null)}
					/>
				</div>
			</Modal>
		</Conditional>
	);
};

export default AutTable;

const initialSortColumn: models.SortColumn = {
	path: '',
	order: 'desc',
};
